import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { Execution } from '../types/attacks';
import { OperationalError } from '../types/operationalErrors';
import {
  mergeOperationalErrors,
  normalizeBotError,
  normalizeExecutionErrors,
  normalizeTransactionError,
} from '../utils/operationalErrors';
import { useAdaptivePolling } from './useAdaptivePolling';

/** Operators and admins only — viewers are not granted `view_operational_errors`. */
export const VIEW_OPERATIONAL_ERRORS = 'view_operational_errors';

/**
 * Whether the current user may see the operational error feed for `tenantId`. Callers use this to
 * hide the entry point entirely rather than rendering a panel that would always be empty.
 */
export const useCanViewOperationalErrors = (tenantId?: string | null): boolean => {
  const { hasPermission, loading } = usePermissions();
  return !loading && hasPermission(VIEW_OPERATIONAL_ERRORS, 'tenant', tenantId || undefined);
};

interface UseOperationalErrorsOptions {
  /** Tenant the feed is scoped to. Nothing is fetched until this is known. */
  tenantId?: string | null;
  /** When set, execution errors are limited to this scenario's runs. */
  scenarioId?: string | null;
  /**
   * Executions the caller already has (the Executions screen loads them anyway). When provided the
   * hook derives execution errors from these instead of issuing its own `/attacks/executions` call.
   */
  executions?: Execution[] | null;
  /** Per-source row cap sent to the backend. */
  limit?: number;
  /** Set false to keep the feed idle (e.g. the panel's screen is not mounted). */
  enabled?: boolean;
}

export interface UseOperationalErrorsResult {
  errors: OperationalError[];
  loading: boolean;
  /** Set only when *every* source failed — a partial failure still renders what did load. */
  error: string | null;
  /** Names of the sources whose fetch failed on the last refresh. */
  degradedSources: string[];
  refresh: () => Promise<void>;
  lastUpdatedMs: number | null;
}

const DEFAULT_LIMIT = 100;

export const useOperationalErrors = ({
  tenantId,
  scenarioId,
  executions,
  limit = DEFAULT_LIMIT,
  enabled = true,
}: UseOperationalErrorsOptions): UseOperationalErrorsResult => {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [botErrors, setBotErrors] = useState<OperationalError[]>([]);
  const [transactionErrors, setTransactionErrors] = useState<OperationalError[]>([]);
  const [fetchedExecutions, setFetchedExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [degradedSources, setDegradedSources] = useState<string[]>([]);
  const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);
  const intervalMs = useAdaptivePolling({ activeMs: 20_000, idleMs: 60_000 });

  const allowed = hasPermission(VIEW_OPERATIONAL_ERRORS, 'tenant', tenantId || undefined);
  const executionsProvided = Array.isArray(executions);
  // Callers hand us a fresh array on every render, so key off the contents that can actually
  // change an error rather than the array identity, and read the latest value through a ref.
  const providedExecutionsKey = executionsProvided
    ? (executions as Execution[]).map((e) => `${e.id}:${e.status}:${e.updated_at}`).join('|')
    : '';
  const providedExecutionsRef = useRef<Execution[]>([]);
  providedExecutionsRef.current = executionsProvided ? (executions as Execution[]) : [];

  const active = enabled && !permissionsLoading && allowed && Boolean(tenantId);
  // Read inside the callback without making it a dependency — flipping `enabled` should not
  // rebuild `load` and restart the poll timer.
  const activeRef = useRef(active);
  activeRef.current = active;

  const load = useCallback(
    async (showSpinner: boolean) => {
      if (!activeRef.current) return;
      if (showSpinner) setLoading(true);
      const failed: string[] = [];

      const [bots, transactions, execs] = await Promise.all([
        api
          .getBotManagerErrors(limit, tenantId || undefined)
          .catch((e) => {
            console.error('Failed to load bot errors:', e);
            failed.push('Bots');
            return null;
          }),
        api
          .getTransactionErrors(limit, tenantId || undefined)
          .catch((e) => {
            console.error('Failed to load transaction errors:', e);
            failed.push('Transactions');
            return null;
          }),
        executionsProvided
          ? Promise.resolve(null)
          : api
              .getExecutions(scenarioId || undefined)
              .catch((e) => {
                console.error('Failed to load executions:', e);
                failed.push('Executions');
                return null;
              }),
      ]);

      if (bots) setBotErrors(bots.map(normalizeBotError));
      if (transactions) setTransactionErrors(transactions.map(normalizeTransactionError));
      if (execs) setFetchedExecutions(execs);

      const attempted = executionsProvided ? 2 : 3;
      setDegradedSources(failed);
      setError(failed.length === attempted ? 'Could not load errors.' : null);
      setLastUpdatedMs(Date.now());
      if (showSpinner) setLoading(false);
    },
    [executionsProvided, limit, scenarioId, tenantId]
  );

  useEffect(() => {
    if (!active) return;
    load(true);
  }, [active, load]);

  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => load(false), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs, load]);

  const errors = useMemo(() => {
    if (!active) return [];
    const source = executionsProvided ? providedExecutionsRef.current : fetchedExecutions;
    const scoped = scenarioId
      ? source.filter((execution) => execution.scenario_id === scenarioId)
      : source;
    return mergeOperationalErrors(
      botErrors,
      transactionErrors,
      ([] as OperationalError[]).concat(...scoped.map(normalizeExecutionErrors))
    );
    // providedExecutionsKey stands in for the ref's contents, which React cannot track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    botErrors,
    transactionErrors,
    fetchedExecutions,
    executionsProvided,
    providedExecutionsKey,
    scenarioId,
  ]);

  const refresh = useCallback(() => load(true), [load]);

  return { errors, loading, error, degradedSources, refresh, lastUpdatedMs };
};

export default useOperationalErrors;
